import React, { useCallback, useEffect, useId, useState } from 'react';
import { AuditMessage } from '../../types';
import { AuditAccessRequest, auditService } from '../../services/auditService';
import { AlertTriangle, Check, Eye, Hash, Send, Trash2, User, X } from 'lucide-react';
import { iconForFileName } from '../../utils/fileIcon';
import { ADMIN_LIST_CAPPED } from './adminLayout';
import { useToast } from '../../contexts/ToastContext';
import { ConfirmDialog } from '../ConfirmDialog';

/**
 * 메시지 감사 — 대화 내용 열람 승인 절차(2026-11, server/services/auditAccess.js).
 *
 * 사유를 적어 요청 → **요청자가 아닌** Super Admin이 시간(1~72시간)을 정해 승인 → 그 시간 동안 그 대상만 [조회].
 * 이용 고지(③ 대화 내용 열람 동의)가 직원에게 약속하는 절차와 같다. 요청·승인·반려·취소·조회가 모두 감사 로그에 남는다.
 * 목록은 `audit:access-changed`(socketService가 재발행)로 다시 읽는다.
 */
type AuditMode = 'channel' | 'user';

const REASON_MIN = 10;
const HOUR_OPTIONS = [1, 4, 8, 24, 72];
const PAGE = 50;

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  pending: { label: '승인 대기', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: '승인됨', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: '반려', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  canceled: { label: '취소', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  expired: { label: '시간 지남', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

function remainingText(validUntil: string | null): string {
  if (!validUntil) return '';
  const ms = new Date(validUntil).getTime() - Date.now();
  if (ms <= 0) return '';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}시간 ${m}분 남음` : `${m}분 남음`;
}

const targetText = (r: AuditAccessRequest) => (r.targetType === 'channel'
  ? `#${r.targetName || r.targetId} (대화방 ${r.targetId})`
  : `${r.targetName || r.targetId} (사번 ${r.targetId})`);

export const MessageAudit: React.FC = () => {
  const fid = useId();
  const { showToast } = useToast();
  const [mode, setMode] = useState<AuditMode>('channel');
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [requests, setRequests] = useState<AuditAccessRequest[]>([]);
  const [canDecide, setCanDecide] = useState(false);
  const [me, setMe] = useState('');
  const [listError, setListError] = useState<string | null>(null);

  // 결정 입력(행마다 하나만 연다)
  const [deciding, setDeciding] = useState<{ id: number; kind: 'approve' | 'reject' } | null>(null);
  const [hours, setHours] = useState(24);
  const [note, setNote] = useState('');
  const [cancelTarget, setCancelTarget] = useState<AuditAccessRequest | null>(null);

  // 열람 결과
  const [viewing, setViewing] = useState<AuditAccessRequest | null>(null);
  const [messages, setMessages] = useState<AuditMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const r = await auditService.listAccessRequests('all');
      setRequests(r.requests);
      setCanDecide(r.canDecide);
      setMe(String(r.me || ''));
      setListError(null);
    } catch (err: any) {
      setListError(err?.message || '열람 요청 목록을 불러오지 못했습니다.');
    }
  }, []);

  useEffect(() => {
    void reload();
    const onChanged = () => { void reload(); };
    window.addEventListener('audit:access-changed', onChanged);
    // 승인 시간이 지나는 것을 보여 주려고 1분마다 다시 그린다(요청은 하지 않는다)
    const tick = setInterval(() => setRequests((prev) => [...prev]), 60000);
    return () => {
      window.removeEventListener('audit:access-changed', onChanged);
      clearInterval(tick);
    };
  }, [reload]);

  const myRequests = requests.filter((r) => r.requesterId === me);
  const toDecide = canDecide ? requests.filter((r) => r.status === 'pending' && r.requesterId !== me) : [];
  const reasonOk = reason.trim().length >= REASON_MIN;

  const submitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetId.trim() || !reasonOk || submitting) return;
    setSubmitting(true);
    try {
      await auditService.requestAccess(mode, targetId.trim(), reason.trim());
      showToast({ type: 'success', message: '열람 승인을 요청했습니다. 다른 Super Admin이 승인하면 조회할 수 있습니다.' });
      setTargetId('');
      setReason('');
      await reload();
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '열람 요청을 보내지 못했습니다.' });
    } finally {
      setSubmitting(false);
    }
  };

  const openDecision = (id: number, kind: 'approve' | 'reject') => {
    setDeciding({ id, kind });
    setHours(24);
    setNote('');
  };

  const submitDecision = async () => {
    if (!deciding) return;
    try {
      if (deciding.kind === 'approve') {
        await auditService.approveAccess(deciding.id, hours, note.trim() || undefined);
        showToast({ type: 'success', message: `${hours}시간 동안 열람을 승인했습니다.` });
      } else {
        if (note.trim().length < 2) {
          showToast({ type: 'error', message: '반려 사유를 적어 주세요.' });
          return;
        }
        await auditService.rejectAccess(deciding.id, note.trim());
        showToast({ type: 'success', message: '열람 요청을 반려했습니다.' });
      }
      setDeciding(null);
      await reload();
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '열람 요청을 처리하지 못했습니다.' });
    }
  };

  const confirmCancel = async () => {
    const target = cancelTarget;
    setCancelTarget(null);
    if (!target) return;
    try {
      await auditService.cancelAccess(target.id);
      if (viewing?.id === target.id) setViewing(null);
      showToast({ type: 'success', message: '열람 요청을 취소했습니다.' });
      await reload();
    } catch (err: any) {
      showToast({ type: 'error', message: err?.message || '열람 요청을 취소하지 못했습니다.' });
    }
  };

  const loadMessages = async (req: AuditAccessRequest, offset: number) => {
    setLoadingMessages(true);
    setViewError(null);
    try {
      const result = req.targetType === 'channel'
        ? await auditService.getChannelMessages(parseInt(req.targetId, 10), req.id, PAGE, offset)
        : await auditService.getUserMessages(req.targetId, req.id, PAGE, offset);
      setMessages((prev) => (offset === 0 ? result.messages : [...prev, ...result.messages]));
      setTotal(result.total);
    } catch (err: any) {
      setViewError(err?.message || '메시지를 불러오지 못했습니다.');
      if (offset === 0) setMessages([]);
      void reload();
    } finally {
      setLoadingMessages(false);
    }
  };

  const startView = (req: AuditAccessRequest) => {
    setViewing(req);
    setMessages([]);
    setTotal(0);
    void loadMessages(req, 0);
  };

  return (
    <div className="space-y-5">
      {/* 열람 요청 */}
      <form onSubmit={submitRequest} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
        <h3 className="text-sm font-semibold text-slate-800">열람 요청</h3>
        <div className="flex gap-2">
          {(['channel', 'user'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => { setMode(m); setTargetId(''); }}
              aria-pressed={mode === m}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                mode === m ? 'bg-brand-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              {m === 'channel' ? <Hash size={14} aria-hidden="true" /> : <User size={14} aria-hidden="true" />}
              {m === 'channel' ? '대화방' : '사용자'}
            </button>
          ))}
        </div>
        <div>
          <label htmlFor={`${fid}-target`} className="block text-xs text-slate-500 mb-1">
            {mode === 'channel' ? '대화방 번호 *' : '사용자 사번 *'}
          </label>
          <input
            id={`${fid}-target`}
            type="text"
            inputMode={mode === 'channel' ? 'numeric' : 'text'}
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            placeholder={mode === 'channel' ? '대화방 번호' : '사번'}
            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor={`${fid}-reason`} className="block text-xs text-slate-500 mb-1">
            열람 사유 * ({REASON_MIN}자 이상 — 근거가 된 신고·요청을 구체적으로)
          </label>
          <textarea
            id={`${fid}-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={1000}
            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent resize-y"
          />
        </div>
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
          <span className="text-xs text-amber-700">
            요청자가 아닌 Super Admin이 승인해야 정해진 시간 동안 조회할 수 있습니다. 요청·승인·조회는 사유와 함께
            감사 로그에 남고 Super Admin에게 통지됩니다.
          </span>
        </div>
        <button
          type="submit"
          disabled={submitting || !targetId.trim() || !reasonOk}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
        >
          <Send size={14} aria-hidden="true" />
          {submitting ? '요청 중…' : '요청'}
        </button>
      </form>

      {listError && (
        <div className="px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded-lg border border-brand-200">{listError}</div>
      )}

      {/* 승인 대기 — Super Admin */}
      {canDecide && (
        <section>
          <h3 className="text-sm font-semibold text-slate-800 mb-2">
            승인 대기 {toDecide.length > 0 && <span className="text-brand-700">{toDecide.length}건</span>}
          </h3>
          {toDecide.length === 0 ? (
            <p className="text-xs text-slate-500">승인할 요청이 없습니다</p>
          ) : (
            <ul className={`space-y-2 ${ADMIN_LIST_CAPPED}`}>
              {toDecide.map((r) => (
                <li key={r.id} className="p-3 rounded-lg border border-amber-200 bg-amber-50/40 text-sm">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="font-medium text-slate-800">{r.requesterName || r.requesterId}</span>
                    <span className="text-xs text-slate-500">→ {targetText(r)}</span>
                    <span className="text-xs text-slate-400">#{r.id} · {formatDate(r.createdAt)}</span>
                  </div>
                  <p className="mt-1 text-slate-700 whitespace-pre-wrap break-anywhere">사유: {r.reason}</p>
                  {deciding?.id === r.id ? (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {deciding.kind === 'approve' && (
                        <select
                          value={hours}
                          onChange={(e) => setHours(Number(e.target.value))}
                          aria-label="승인 시간"
                          className="px-2 py-1.5 text-sm bg-white border border-slate-300 rounded-lg"
                        >
                          {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}시간</option>)}
                        </select>
                      )}
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        maxLength={500}
                        placeholder={deciding.kind === 'approve' ? '메모(선택)' : '반려 사유(필수)'}
                        aria-label={deciding.kind === 'approve' ? '승인 메모' : '반려 사유'}
                        className="flex-1 min-w-[10rem] px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => void submitDecision()}
                        className={`px-3 py-1.5 text-sm rounded-lg ${deciding.kind === 'approve'
                          ? 'bg-brand-600 text-white hover:bg-brand-700'
                          : 'bg-white border border-brand-600 text-brand-600 hover:bg-brand-50'}`}
                      >
                        {deciding.kind === 'approve' ? '승인' : '반려'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeciding(null)}
                        className="px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 rounded-lg"
                      >
                        취소
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => openDecision(r.id, 'approve')}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700"
                      >
                        <Check size={14} aria-hidden="true" />
                        승인
                      </button>
                      <button
                        type="button"
                        onClick={() => openDecision(r.id, 'reject')}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border border-brand-600 text-brand-600 rounded-lg hover:bg-brand-50"
                      >
                        <X size={14} aria-hidden="true" />
                        반려
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* 내 요청 */}
      <section>
        <h3 className="text-sm font-semibold text-slate-800 mb-2">내 요청</h3>
        {myRequests.length === 0 ? (
          <p className="text-xs text-slate-500">보낸 열람 요청이 없습니다</p>
        ) : (
          <ul className={`space-y-2 ${ADMIN_LIST_CAPPED}`}>
            {myRequests.map((r) => {
              const st = STATUS_LABEL[r.status] || STATUS_LABEL.pending;
              const remain = r.status === 'approved' ? remainingText(r.validUntil) : '';
              return (
                <li key={r.id} className="p-3 rounded-lg border border-slate-200 bg-white text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${st.cls}`}>{st.label}</span>
                    <span className="font-medium text-slate-800">{targetText(r)}</span>
                    <span className="text-xs text-slate-400">#{r.id} · {formatDate(r.createdAt)}</span>
                    {remain && <span className="text-xs text-emerald-700">{remain}</span>}
                  </div>
                  <p className="mt-1 text-xs text-slate-600 whitespace-pre-wrap break-anywhere">사유: {r.reason}</p>
                  {(r.status === 'approved' || r.status === 'rejected') && r.decidedByName && (
                    <p className="mt-0.5 text-xs text-slate-500">
                      {r.status === 'approved' ? '승인' : '반려'}: {r.decidedByName} · {formatDate(r.decidedAt)}
                      {r.decisionNote ? ` — ${r.decisionNote}` : ''}
                    </p>
                  )}
                  {(r.status === 'approved' || r.status === 'pending') && (
                    <div className="mt-2 flex gap-2">
                      {r.status === 'approved' && (
                        <button
                          type="button"
                          onClick={() => startView(r)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700"
                        >
                          <Eye size={14} aria-hidden="true" />
                          조회
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setCancelTarget(r)}
                        className="px-3 py-1.5 text-sm bg-white border border-brand-600 text-brand-600 rounded-lg hover:bg-brand-50"
                      >
                        {r.status === 'approved' ? '열람 종료' : '요청 취소'}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* 조회 결과 */}
      {viewing && (
        <section className="rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
            <span className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">{targetText(viewing)}</span> · 총 {total}건 · 승인 #{viewing.id}
            </span>
            <button
              type="button"
              onClick={() => setViewing(null)}
              aria-label="조회 결과 닫기"
              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <div className="p-3">
            {viewError && (
              <div className="mb-2 px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded-lg border border-brand-200">{viewError}</div>
            )}
            {!loadingMessages && !viewError && messages.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-500">조회된 메시지가 없습니다</p>
            )}
            <div className={`space-y-1.5 ${ADMIN_LIST_CAPPED}`}>
              {messages.map((msg) => (
                <div
                  key={`${msg.id}-${msg.attachment?.filePath || ''}`}
                  className={`p-3 rounded-lg border text-sm ${msg.deletedAt ? 'bg-brand-50 border-brand-200' : 'bg-white border-slate-200'}`}
                >
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-slate-800">{msg.senderName}</span>
                      <span className="text-xs text-slate-400">{msg.senderId}</span>
                      {msg.channelName && <span className="text-xs text-brand-700 truncate">#{msg.channelName}</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {msg.deletedAt && (
                        <span className="flex items-center gap-1 text-xs text-brand-700">
                          <Trash2 size={12} aria-hidden="true" />
                          삭제됨
                        </span>
                      )}
                      {msg.attachment && (() => {
                        const TypeIcon = iconForFileName(msg.attachment.fileName);
                        return (
                          <span className="flex items-center gap-1 text-xs text-brand-700">
                            <TypeIcon size={12} />
                            {msg.attachment.fileName}
                          </span>
                        );
                      })()}
                      <span className="text-xs text-slate-400">{formatDate(msg.createdAt)}</span>
                    </div>
                  </div>
                  <div className="text-slate-700 whitespace-pre-wrap break-anywhere">{msg.content}</div>
                </div>
              ))}
            </div>
            {messages.length > 0 && messages.length < total && (
              <button
                type="button"
                onClick={() => void loadMessages(viewing, messages.length)}
                disabled={loadingMessages}
                className="mt-2 w-full px-3 py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg disabled:opacity-50"
              >
                {loadingMessages ? '불러오는 중…' : '더 보기'}
              </button>
            )}
            {loadingMessages && messages.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-500">불러오는 중…</p>
            )}
          </div>
        </section>
      )}

      <ConfirmDialog
        isOpen={!!cancelTarget}
        title={cancelTarget?.status === 'approved' ? '열람 종료' : '요청 취소'}
        message={cancelTarget?.status === 'approved'
          ? '승인 시간이 남아 있어도 지금 열람을 끝낼까요? 다시 보려면 새로 요청해야 합니다.'
          : '이 열람 요청을 취소할까요?'}
        confirmLabel={cancelTarget?.status === 'approved' ? '종료' : '취소'}
        cancelLabel="닫기"
        danger
        onConfirm={() => void confirmCancel()}
        onCancel={() => setCancelTarget(null)}
      />
    </div>
  );
};
